// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { PropsWithChildren } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Typography,
} from '@mui/material';
import { GridExpandMoreIcon } from '@mui/x-data-grid';
import { CodeBlock } from '../components/CodeBlock';
import Loading from '../components/Loading';
import usePrimaryParty from '../hooks/queries/usePrimaryParty';
import { ModuleContext, ModuleState, SigningMode } from './ModuleContext';
import { useUserState } from './UserProvider';

interface ModuleProviderProps {
  value: ModuleState;
}

const Provider: React.FC<PropsWithChildren<ModuleProviderProps>> = ({ value, children }) => (
  <ModuleContext.Provider value={value}>{children}</ModuleContext.Provider>
);

const SelfHostedModule: React.FC<PropsWithChildren> = ({ children }) => {
  const party = usePrimaryParty();
  const { logout } = useUserState();

  if (party.isLoading) {
    return <Loading />;
  }

  if (party.isError) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        width="100%"
        height="100%"
      >
        <p>
          Unable to find the party associated with your login. Please contact the provider of this
          application for support.
        </p>
        <Box padding={2}>
          <Accordion>
            <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
              <Typography>Error Details</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ backgroundColor: 'grey.100' }}>
              <CodeBlock>
                {JSON.stringify({
                  name: party.error.name,
                  message: party.error.message,
                })}
              </CodeBlock>
            </AccordionDetails>
          </Accordion>
        </Box>

        <Button
          onClick={() => {
            logout();
          }}
        >
          Log Out
        </Button>
      </Box>
    );
  }

  return (
    <Provider value={{ activeParty: party.data, activeSigningMode: SigningMode.LOCAL }}>
      {children}
    </Provider>
  );
};

export const ModuleProvider: React.FC<PropsWithChildren> = ({ children }) => {
  return <SelfHostedModule>{children}</SelfHostedModule>;
};
